import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { loadStores, tzForRegion } from "../../lib/timezone";
import { toast } from "sonner";
import { apiErrorMessage } from "../../lib/inventoryApi";
import { cariKaryawan } from "../../lib/sumberPilihan";

// Pembantu bersama halaman aset (4b-1). Nol aturan bisnis di sini — hanya
// pemuat referensi (karyawan, region, store) dan "hari ini" per zona region.

/** Sumber PemilihCari: karyawan AKTIF satu region (PIC aset/jadwal, pelaksana, pelapor, pembaca km;
 *  PIC wajib, vonis Owner 15 Sep: karyawan region aset, tak wajib ber-akun).
 *  Pola daftar seragam (28 Sep 2026): DICARI DI SERVER (GET /api/hr/employees?q=&region_id=&limit=20),
 *  bukan lagi seluruh karyawan region dimuat ke satu dropdown. */
export const cariKaryawanRegion = (regionId) => (kata) =>
  cariKaryawan(kata, { active: true, ...(regionId ? { region_id: regionId } : {}) });

/** Satu karyawan per id — nilai PemilihCari yang tersimpan sebagai id (GET /api/hr/employees/{id}). */
export const ambilKaryawan = async (id) => (await api.get(`/hr/employees/${id}`)).data;

/** Nama karyawan (termasuk nonaktif) untuk id-id yang tampil di layar — menamai PIC/pelaksana lama di riwayat.
 *  Satu panggilan `GET /api/hr/employees?ids=…` (≤ 200 id), bukan seluruh karyawan region. Peran yang tidak
 *  berhak membaca karyawan (403) → daftar kosong, nama jatuh ke "—" seperti sebelumnya.
 *  Mengembalikan larik yang dipakai `namaKaryawan(larik, id)`. */
export function useNamaKaryawan(ids) {
  const kunci = [...new Set((ids || []).filter(Boolean))].sort().slice(0, 200).join(",");
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!kunci) { setItems([]); return undefined; }
    let hidup = true;
    api.get("/hr/employees", { params: { ids: kunci, limit: 200 } })
      .then((r) => { if (hidup) setItems(r.data?.items || []); })
      .catch((e) => {
        if (!hidup) return;
        setItems([]);
        if (e?.response?.status !== 403) toast.error(apiErrorMessage(e, "Nama karyawan gagal dimuat."));
      });
    return () => { hidup = false; };
  }, [kunci]);
  return items;
}

/** Daftar region — hanya dimuat bila `aktif` (OWNER). */
export function useRegions(aktif) {
  const [regions, setRegions] = useState([]);
  useEffect(() => {
    if (!aktif) return undefined;
    let hidup = true;
    api.get("/regions").then((r) => { if (hidup) setRegions(r.data || []); }).catch((e) => toast.error(apiErrorMessage(e, "Data pendukung gagal dimuat.")));
    return () => { hidup = false; };
  }, [aktif]);
  return regions;
}

/** Daftar store yang boleh dilihat pemanggil (dijepit wilayah untuk KC/ADMIN). */
export function useStores() {
  const [stores, setStores] = useState([]);
  useEffect(() => {
    let hidup = true;
    api.get("/stores").then((r) => { if (hidup) setStores(Array.isArray(r.data) ? r.data : []); }).catch((e) => toast.error(apiErrorMessage(e, "Data pendukung gagal dimuat.")));
    return () => { hidup = false; };
  }, []);
  return stores;
}

/** "Hari ini" (YYYY-MM-DD) menurut zona region — cermin RPC
 *  `aset_hari_ini_region`. Zona dari peta store region (lib/timezone);
 *  region tanpa zona bulat suara → null (pemanggil biarkan backend mengisi). */
export async function hariIniRegion(regionId) {
  await loadStores();
  const tz = tzForRegion(regionId);
  if (!tz) return null;
  try {
    // en-CA menghasilkan YYYY-MM-DD.
    return new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" })
      .format(new Date());
  } catch {
    return null;
  }
}

export function namaKaryawan(karyawan, id) {
  if (!id) return "—";
  const k = karyawan.find((x) => x.id === id);
  return k ? `${k.full_name}${k.employee_code ? ` (${k.employee_code})` : ""}` : "—";
}

export function formatKm(n) {
  if (n === null || n === undefined) return "—";
  return `${Number(n).toLocaleString("id-ID")} km`;
}

const BULAN_ID = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
/** "2026-09-01" → "September 2026" (nama bulan Indonesia — bukan keputusan bisnis, cuma format). */
export function periodeLabel(p) {
  if (!p) return "—";
  const s = String(p);
  const b = parseInt(s.slice(5, 7), 10);
  return Number.isNaN(b) ? s : `${BULAN_ID[b - 1]} ${s.slice(0, 4)}`;
}
